/**
 * useLockAssets — load GLB (from DB via API) + features JSON when
 * a lock is selected in the lock calculator form.
 *
 * ## Flow
 * 1. `connectorPairLockItemIds` change → directly use the lock item UUID
 * 2. Fetch GLB authenticated by `GET /api/modules/lock_calculator/lock/{itemId}/model`
 * 3. Create URL blobs and preload with `useGLTF.preload()`
 * 4. Exposes data for 3D scene
 *
 * ##Logging
 * In dev mode, each upload is tracked in the console with the [Assets] prefix.*/
import { useGLTF } from "@react-three/drei";
import { useEffect, useRef, useState } from "react";
import { useApiHeaders } from "../../../../../hooks/useApiHeaders";
import type { ApiHeaders } from "../../../../../http/apiHeaders";
import type { LockSceneInput } from "../LockScene.types";

// ─── Logging ────────────────────────────────────────────────

const LOG_PREFIX = "[Assets]";
const isDev = import.meta.env.DEV;

function log(level: "log" | "warn" | "error", msg: string, data?: unknown): void {
  if (isDev) {
    // eslint-disable-next-line no-console
    console[level](`${LOG_PREFIX} ${msg}`, data ?? "");
  }
}

// ─── Cache blob URL ─────────────────────────────────────────
//Avoid refetching GLBs already loaded in the session

// Cache della PROMISE, non dell'URL risolto: sotto StrictMode (doppio mount) o
// piu' mount concorrenti le chiamate partivano prima che la cache fosse
// popolata, creando due blob per lo stesso asset (il primo poi perso).
//
// LRU con eviction (29-09-2026, richiesta utente: "stiamo saturando la
// memoria, bisogna liberare la GPU"): senza limite, ogni asset diverso
// provato in sessione (utile proprio durante l'indagine lockstop, con
// decine di combinazioni connettore/lockstop testate) resta in cache PER
// SEMPRE — sia il blob URL (`URL.createObjectURL`, memoria del processo) sia
// la geometria caricata da drei (`useGLTF`, buffer GPU) non vengono MAI
// liberati. `Map` mantiene l'ordine di inserimento: lo si sfrutta come
// ordine LRU spostando in coda le chiavi riusate (`touchCache`) e liberando
// le piu' vecchie oltre `MAX_CACHED_ASSETS` (`evictOldestIfNeeded`).
//
// Il limite ha margine sopra i 6 slot GLB POSSIBILI in scena contemporaneamente
// (connettore 1, connettore 2, vite joist, vite header, vite diagonale,
// lockstop — le travi sono parametriche, nessun GLB): con 6 slot attivi la
// selezione corrente resta sempre in coda (`touchCache` la protegge
// dall'eviction), quindi il margine serve solo a non rifare fetch di
// combinazioni appena abbandonate.
const blobUrlCache = new Map<string, Promise<string | null>>();
const MAX_CACHED_ASSETS = 14;

/** Sposta `key` in coda (piu' recente) se gia' presente — no-op altrimenti. */
function touchCache(key: string): void {
  const cached = blobUrlCache.get(key);
  if (!cached) return;
  blobUrlCache.delete(key);
  blobUrlCache.set(key, cached);
}

/**
 * Libera le voci piu' vecchie oltre `MAX_CACHED_ASSETS`: revoca il blob URL
 * e sgancia la geometria dalla cache di drei (`useGLTF.clear`), cosi' il
 * garbage collector puo' liberare davvero i buffer GPU. Asincrona perche' la
 * entry in cache e' una Promise, non l'URL gia' risolto.*/
async function evictOldestIfNeeded(): Promise<void> {
  while (blobUrlCache.size > MAX_CACHED_ASSETS) {
    const oldestKey = blobUrlCache.keys().next().value;
    if (oldestKey === undefined) break;
    const promise = blobUrlCache.get(oldestKey);
    blobUrlCache.delete(oldestKey);
    const url = await promise?.catch(() => null);
    if (url) {
      useGLTF.clear(url);
      URL.revokeObjectURL(url);
      log("log", `GLB evicted from cache (limite ${MAX_CACHED_ASSETS} asset): ${oldestKey}`);
    }
  }
}

/**
 * Fetch a GLB from the API and create a URL blob.
 * Deduplica per chiave: chiamate concorrenti condividono la stessa promise.*/
function fetchGlbBlobUrl(
  assetType: "lock" | "fastener" | "lockstop",
  itemId: string,
  apiHeaders: ApiHeaders,
): Promise<string | null> {
  const cacheKey = `${assetType}:${itemId}`;
  const cached = blobUrlCache.get(cacheKey);
  if (cached) {
    touchCache(cacheKey);
    return cached;
  }

  const inflight = (async (): Promise<string | null> => {
    try {
      const resource = assetType === "lock" ? "lock" : assetType === "fastener" ? "fasteners" : "lock-stops";
      const response = await fetch(`/api/modules/lock_calculator/${resource}/${itemId}/model`, {
        credentials: "include",
        redirect: "error",
        headers: apiHeaders,
      });
      if (response.status === 204) {
        log("log", `GLB unavailable: ${cacheKey}`);
        return null;
      }
      if (!response.ok) throw new Error(`GLB request failed with status ${response.status}`);
      const blob = await response.blob();
      if (blob.size === 0) return null;
      const url = URL.createObjectURL(blob);
      log("log", `GLB blob URL created: ${cacheKey}`);
      return url;
    } catch (err) {
      // Non lasciare in cache un fetch fallito: un mount successivo deve poter riprovare.
      blobUrlCache.delete(cacheKey);
      log("error", `GLB fetch error: ${cacheKey}`, err);
      return null;
    }
  })();

  blobUrlCache.set(cacheKey, inflight);
  void inflight.then(() => evictOldestIfNeeded());
  return inflight;
}

// ─── Hook ───────────────────────────────────────────────────

export interface LockAssets {
  /** First connector's GLB Lock URL blob (for use with useGLTF)*/
  connGlbUrl1: string | null;
  /** Second connector's GLB Lock URL blob*/
  connGlbUrl2: string | null;
  /** GLB of the wider lock replacing the header half; null = none. */
  headerLockGlbUrl: string | null;
  lockstopGlbUrl: string | null;
  headFastenerGlbUrl: string | null;
  joistFastenerGlbUrl: string | null;
  inclinedFastenerGlbUrl: string | null;
}

/**
 * Hook that observes form selections and loads automatically
 * the GLBs associated with the selected Locks.*/
export function useLockAssets(input: LockSceneInput): LockAssets {
  const apiHeaders = useApiHeaders();
  const connectorsId = input.connectorPairLockItemIds;
  const lockstopId = input.lockstopItemId;
  const headerLockId = input.headerLockItemId ?? null;
  const headFastenerItemId = input.fasteners.headerItemId;
  const joistFastenerItemId = input.fasteners.joistItemId;
  const inclinedFastenerItemId = input.fasteners.inclinedItemId;

  const [connGlbUrl1, setConnGlbUrl1] = useState<string | null>(null);
  const [connGlbUrl2, setConnGlbUrl2] = useState<string | null>(null);
  const [headerLockGlbUrl, setHeaderLockGlbUrl] = useState<string | null>(null);
  const [lockstopGlbUrl, setLockstopGlbUrl] = useState<string | null>(null);
  const [headFastenerGlbUrl, setHeadFastenerGlbUrl] = useState<string | null>(null);
  const [joistFastenerGlbUrl, setJoistFastenerGlbUrl] = useState<string | null>(null);
  const [inclinedFastenerGlbUrl, setInclinedFastenerGlbUrl] = useState<string | null>(null);

  const loadingIdRef = useRef(0);

  // ─── Effetto principale: carica GLB ─────────────────────
  useEffect(() => {
    const loadingId = ++loadingIdRef.current;
    let isCurrent = true;

    const loadAll = async () => {
      const [connUrl1, connUrl2, headerLockUrl, lockstopUrl, headFastenerUrl, joistFastenerUrl, inclinedFastenerUrl] =
        await Promise.all([
          connectorsId[0] ? fetchGlbBlobUrl("lock", connectorsId[0], apiHeaders) : Promise.resolve(null),
          input.connectorPairCount === 2 && connectorsId[1]
            ? fetchGlbBlobUrl("lock", connectorsId[1], apiHeaders)
            : Promise.resolve(null),
          headerLockId ? fetchGlbBlobUrl("lock", headerLockId, apiHeaders) : Promise.resolve(null),

          lockstopId ? fetchGlbBlobUrl("lockstop", lockstopId, apiHeaders) : Promise.resolve(null),

          //Temporary debug: Don't load fastener GLBs. When an ID is
          //selected, resolveScene still creates the anchor and Fastener uses the
          // its own parametric fallback because glbUrl remains null.
          // headFastenerItemId ? fetchGlbBlobUrl("fastener", headFastenerItemId, apiHeaders) : Promise.resolve(null),
          Promise.resolve(null),
          // joistFastenerItemId ? fetchGlbBlobUrl("fastener", joistFastenerItemId, apiHeaders) : Promise.resolve(null),
          Promise.resolve(null),
          // inclinedFastenerItemId ? fetchGlbBlobUrl("fastener", inclinedFastenerItemId, apiHeaders) : Promise.resolve(null),
          Promise.resolve(null),
        ]);

      // Evita race condition
      if (!isCurrent || loadingId !== loadingIdRef.current) return;

      // Precarica GLB in cache drei (usa blob URL)
      [connUrl1, connUrl2, headerLockUrl, lockstopUrl, headFastenerUrl, joistFastenerUrl, inclinedFastenerUrl].forEach((url) => {
        if (!url) return;
        try {
          useGLTF.preload(url);
        } catch {
          /* ignore */
        }
      });
      setConnGlbUrl1(connUrl1);
      setConnGlbUrl2(connUrl2);
      setHeaderLockGlbUrl(headerLockUrl);
      setLockstopGlbUrl(lockstopUrl);
      setHeadFastenerGlbUrl(headFastenerUrl);
      setJoistFastenerGlbUrl(joistFastenerUrl);
      setInclinedFastenerGlbUrl(inclinedFastenerUrl);

      log("log", "lock asset loading complete", { primary: !!connUrl1, secondary: !!connUrl2 });
    };

    loadAll().catch((err) => {
      if (isCurrent && loadingId === loadingIdRef.current) {
        log("error", "asset loading failed", err);
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [
    connectorsId[0],
    connectorsId[1],
    headerLockId,
    lockstopId,
    headFastenerItemId,
    joistFastenerItemId,
    inclinedFastenerItemId,
    apiHeaders,
    input.connectorPairCount,
  ]);

  return {
    connGlbUrl1: connGlbUrl1,
    connGlbUrl2: connGlbUrl2,
    headerLockGlbUrl,
    lockstopGlbUrl,
    headFastenerGlbUrl,
    joistFastenerGlbUrl,
    inclinedFastenerGlbUrl,
  };
}
